import {
  Button,
  Css,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Palette,
  Tooltip,
  TreeSelectField,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import {
  buildDuplicateTargets,
  duplicateProductToTargets,
  DuplicateTargetLeaf,
  FormValue,
  isMaterialExpanded,
  PackageOptionRow,
  ProductMaterial,
} from "src/routes/libraries/design-catalog/personalization-option/utils";

type DuplicateVariantModalProps = {
  formState: ObjectState<FormValue>;
  materialVariantId: string;
  featureName: string;
  productMaterial: ProductMaterial;
  /** The package option the duplicate action was triggered from — marked in the tree so it's clear which is the source. */
  sourcePackageOption: PackageOptionRow;
  /** Undefined when the feature is grouped — the source's shared, locationless cell. */
  sourceLocationId: string | undefined;
};

/** "Duplicate to" — copies a product cell's material into other empty slots for the same feature. */
export function DuplicateVariantModal(props: DuplicateVariantModalProps) {
  const tid = useTestIds(props, "duplicateVariantModal");
  const { formState, materialVariantId, featureName, productMaterial, sourcePackageOption, sourceLocationId } = props;
  const { closeModal } = useModal();
  const [selected, setSelected] = useState<string[]>([]);

  // Ungrouped features offer one slot per location; grouped features offer one shared slot per package option.
  const expanded = isMaterialExpanded(formState.packageOptions.value, materialVariantId);

  const { options, groupOptions, disabledOptions, leavesByKey } = useMemo(() => {
    const groups = buildDuplicateTargets(formState, materialVariantId);
    const locationLabels = new Map(
      formState.placeholders.rows
        .filter((p) => p.data.value.materialVariant?.id === materialVariantId)
        .map((p) => [p.data.value.location.id, p.data.value.location.displayLocationPath] as const),
    );
    const leavesByKey = new Map<string, DuplicateTargetLeaf>();
    const disabledOptions: { value: string; reason: string }[] = [];
    // Package option names are the (non-selectable) groups; their slots/locations are the selectable leaves.
    const options = groups.map((group, i) => {
      const groupKey = `po-${i}`;
      const isSourceGroup = group.packageOption === sourcePackageOption;
      return {
        value: groupKey,
        label: group.packageOptionName || "Unnamed package option",
        isSourceGroup,
        isGroupedLeaf: false,
        children: group.leaves.map((leaf) => {
          const leafKey = `${groupKey}:${leaf.locationId ?? "grouped"}`;
          leavesByKey.set(leafKey, leaf);
          const isSourceLeaf = isSourceGroup && leaf.locationId === sourceLocationId;
          if (isSourceLeaf) {
            disabledOptions.push({ value: leafKey, reason: "Duplication source slot." });
          } else if (!leaf.isEmpty) {
            disabledOptions.push({ value: leafKey, reason: "Variants can only be copied into empty slots." });
          }
          const label =
            leaf.locationId != null ? `${featureName} - ${locationLabels.get(leaf.locationId) ?? ""}` : featureName;
          // A grouped/collapsed leaf represents every location for this package option at once.
          return { value: leafKey, label, isSourceGroup: false, isGroupedLeaf: leaf.locationId == null };
        }),
      };
    });
    return { options, groupOptions: options.map((o) => o.value), disabledOptions, leavesByKey };
  }, [formState, materialVariantId, featureName, sourcePackageOption, sourceLocationId]);

  return (
    <>
      <ModalHeader>Duplicate variant</ModalHeader>
      <ModalBody>
        <TreeSelectField
          {...tid.duplicateTo}
          defaultCollapsed
          label="Duplicate to"
          options={options}
          groupOptions={groupOptions}
          disabledOptions={disabledOptions}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          getOptionMenuLabel={(o) => {
            if (o.isSourceGroup) {
              return (
                <span css={Css.df.aic.gap1.$}>
                  {o.label}
                  <Tooltip title="Duplication source">
                    <Icon icon="starFilled" color={Palette.Yellow500} inc={2} />
                  </Tooltip>
                </span>
              );
            }
            // Show same "grouped" indicator as the grid's column header
            if (o.isGroupedLeaf) {
              return (
                <span css={Css.df.aic.gap1.$}>
                  {o.label}
                  <Icon icon="unlink" inc={2} />
                </span>
              );
            }
            return o.label;
          }}
          values={selected}
          onSelect={({ leaf }) => setSelected(leaf.values)}
          placeholder={expanded ? "Select slot" : "Select package option"}
        />
      </ModalBody>
      <ModalFooter>
        <Button {...tid.duplicateCancel} variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          {...tid.duplicateSave}
          variant="primary"
          label="Save"
          disabled={selected.isEmpty}
          onClick={() => {
            const targets = selected.map((key) => leavesByKey.get(key)!);
            duplicateProductToTargets(materialVariantId, productMaterial, targets);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}
